import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Camera } from 'lucide-react';

const GallerySection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [
    {
      src: "/images/president_portrait_1.jpeg",
      title: "Portrait officiel du Président",
      description: "Photo officielle de Son Excellence Bassirou Diomaye Faye"
    },
    {
      src: "/images/president_portrait_3.jpeg",
      title: "Campagne présidentielle 2024",
      description: "Le candidat Bassirou Diomaye Faye durant la campagne électorale"
    },
    {
      src: "/images/president_portrait_6.jpeg",
      title: "Rencontre internationale",
      description: "Le Président lors d'une rencontre diplomatique à Yokohama"
    },
    {
      src: "/images/senegal_palais_2.jpeg",
      title: "Palais de la République",
      description: "Le siège de la Présidence de la République du Sénégal"
    },
    {
      src: "/images/senegal_palais_4.jpeg",
      title: "Architecture présidentielle",
      description: "Vue extérieure du Palais présidentiel à Dakar"
    },
    {
      src: "/images/senegal_flag_2.jpeg",
      title: "Drapeau national",
      description: "Les couleurs nationales du Sénégal : vert, jaune et rouge"
    }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  return (
    <section id="galerie" className="py-20 bg-gradient-card">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* En-tête de section */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Galerie de Photos
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Découvrez les moments marquants de la présidence de Bassirou Diomaye Faye 
              à travers une sélection de photographies officielles.
            </p>
          </div>

          {/* Carrousel principal */}
          <div className="mb-12">
            <Card className="shadow-elegant overflow-hidden">
              <div className="relative">
                {/* Image principale */}
                <div className="aspect-video md:aspect-[16/10] overflow-hidden">
                  <img 
                    src={images[currentImageIndex].src}
                    alt={images[currentImageIndex].title}
                    className="w-full h-full object-cover transition-all duration-500"
                  />
                </div>

                {/* Overlay avec informations */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
                  <h3 className="text-xl md:text-2xl font-semibold mb-2">
                    {images[currentImageIndex].title}
                  </h3>
                  <p className="text-white/90">
                    {images[currentImageIndex].description}
                  </p>
                </div>

                {/* Boutons de navigation */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white"
                  onClick={prevImage}
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white"
                  onClick={nextImage}
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>

                {/* Indicateur de position */}
                <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </div>
            </Card>
          </div>

          {/* Miniatures */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-12">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                className={`relative aspect-square overflow-hidden rounded-lg transition-all duration-300 ${
                  index === currentImageIndex 
                    ? 'ring-4 ring-primary shadow-elegant' 
                    : 'hover:ring-2 hover:ring-primary/50 hover:shadow-card'
                }`}
              >
                <img 
                  src={image.src}
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
                {index === currentImageIndex && (
                  <div className="absolute inset-0 bg-primary/20"></div>
                )}
              </button>
            ))}
          </div>

          {/* Statistiques de la galerie */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <Camera className="w-8 h-8 mx-auto mb-3 text-primary" />
                <div className="text-2xl font-bold text-primary mb-1">{images.length}</div>
                <div className="text-sm text-muted-foreground">Photos officielles</div>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <div className="w-8 h-8 mx-auto mb-3 bg-secondary/20 rounded-full flex items-center justify-center">
                  <span className="text-secondary font-bold">2024</span>
                </div>
                <div className="text-2xl font-bold text-primary mb-1">Depuis</div>
                <div className="text-sm text-muted-foreground">Investiture</div>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <div className="w-8 h-8 mx-auto mb-3 bg-accent/20 rounded-full flex items-center justify-center">
                  <span className="text-accent font-bold">🏛️</span>
                </div>
                <div className="text-2xl font-bold text-primary mb-1">Officiel</div>
                <div className="text-sm text-muted-foreground">Présidence</div>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <div className="w-8 h-8 mx-auto mb-3 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold">🇸🇳</span>
                </div>
                <div className="text-2xl font-bold text-primary mb-1">Sénégal</div>
                <div className="text-sm text-muted-foreground">République</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;